import { buildSpawnTimeline } from './shared/chart';
import {
  CAMERA_STREAK_TIERS,
  GAME_CONFIG,
  GAME_STAGES,
  getCameraTierForStreak,
  getStageById,
} from './gameConfig';
import { CHORD_PROGRESSION } from './music';

const BASS_PATTERN = [1, 0, 0.5, 0, 1, 0, 1, 0, 1, 0, 0.5, 0, 1, 0, 1, 0];
const ARP_MULTIPLIERS = [1, 1.5, 2, 1.5];
const LEAD_PATTERN = [2, 0, 0, 1.5, 0, 1.2, 0, 0, 1.5, 0, 2, 0, 0, 1.5, 1.2, 0];
const buildAudioStageTimelines = () => {
  const stageTimelines = {};

  GAME_STAGES.forEach((stage) => {
    stageTimelines[stage.id] = Array.from({ length: 64 }, (_, step) => {
      const bar = Math.floor(step / 16);
      const beatInBar = step % 16;
      const rootFreq = CHORD_PROGRESSION[bar];
      const events = [];

      if (beatInBar % 4 === 0) events.push({ action: 'kick' });
      if (beatInBar % 8 === 4) events.push({ action: 'snare' });
      if (beatInBar % 2 === 0) {
        events.push({ action: 'hat' });
      }

      const bassMultiplier = BASS_PATTERN[beatInBar];
      if (bassMultiplier > 0) {
        events.push({
          action: 'synth',
          freq: rootFreq * bassMultiplier * 0.5,
          wave: 'sawtooth',
          dur: 0.15,
          vol: 0.12,
        });
      }

      if (stage.audio.enableAccent && beatInBar % 4 === 2) {
        events.push({ action: 'synth', freq: rootFreq * 3, wave: 'square', dur: 0.05, vol: 0.06 });
      }

      if (stage.audio.enableShimmer && beatInBar % 2 !== 0) {
        events.push({ action: 'synth', freq: 9000, wave: 'triangle', dur: 0.02, vol: 0.03 });
      }

      if (stage.audio.enableArpLayer) {
        const arpMultiplier = ARP_MULTIPLIERS[beatInBar % 4];
        events.push({
          action: 'synth',
          freq: rootFreq * arpMultiplier * 2,
          wave: 'square',
          dur: 0.1,
          vol: 0.05,
          minStreak: GAME_CONFIG.timeMilestones[1],
        });
      }

      if (stage.audio.enableLeadLayer) {
        const leadMultiplier = LEAD_PATTERN[beatInBar];
        if (leadMultiplier > 0) {
          events.push({
            action: 'synth',
            freq: rootFreq * leadMultiplier * 2,
            wave: 'square',
            dur: 0.2,
            vol: 0.08,
            minStreak: GAME_CONFIG.timeMilestones[2],
          });
        }
      }

      return events;
    });
  });

  return stageTimelines;
};

const buildCameraTimelineByStage = (sampleRate = 30) => {
  const timelineByStage = {};

  GAME_STAGES.forEach((stage) => {
    const duration = Math.max(stage.endTime - stage.startTime, 1 / sampleRate);
    const sampleCount = Math.max(1, Math.ceil(duration * sampleRate) + 1);
    const tiers = {};

    CAMERA_STREAK_TIERS.forEach((tier) => {
      const tierConfig = stage.camera.tiers[tier.id];
      tiers[tier.id] = Array.from({ length: sampleCount }, (_, index) => {
        const localTime = index / sampleRate;
        const absoluteTime = stage.startTime + localTime;

        return {
          targetX: stage.camera.baseX + Math.sin(absoluteTime * tierConfig.swayFreq) * tierConfig.swayAmpX,
          targetY: stage.camera.baseY + Math.sin(absoluteTime * tierConfig.bobFreq) * tierConfig.bobAmpY,
          targetZ: stage.camera.baseZ,
          // Keep the lens fixed: progressive camera tiers may sway or bob,
          // but they never zoom toward/away from the runner.
          targetFov: stage.camera.baseFov,
          targetRotZ: Math.sin(absoluteTime * tierConfig.swayFreq) * tierConfig.rotZAmp,
        };
      });
    });

    timelineByStage[stage.id] = tiers;
  });

  return { sampleRate, byStage: timelineByStage };
};

export const buildSessionTimelines = ({ speedMultiplier, seed = 0 }) => {
  const effectiveBPM = GAME_CONFIG.bpm * speedMultiplier;
  const beatInterval = 60 / effectiveBPM;
  const sixteenthInterval = beatInterval / 4;

  return {
    spawnTimeline: buildSpawnTimeline(sixteenthInterval, seed),
    audioStageTimelines: buildAudioStageTimelines(),
    cameraTimeline: buildCameraTimelineByStage(),
  };
};

export const getCameraSampleForTime = (cameraTimeline, stageId, streakTime, elapsedTime, output = {}) => {
  const stage = getStageById(stageId);
  const tier = getCameraTierForStreak(streakTime);
  const stageTimeline = cameraTimeline?.byStage?.[stage.id];
  const samples = stageTimeline?.[tier.id] ?? stageTimeline?.base;

  if (!samples || samples.length === 0) {
    return {
      targetX: stage.camera.baseX,
      targetY: stage.camera.baseY,
      targetZ: stage.camera.baseZ,
      targetFov: stage.camera.baseFov,
      targetRotZ: 0,
    };
  }

  const localTime = Math.max(0, Math.min(stage.endTime - stage.startTime, elapsedTime - stage.startTime));
  const position = Math.min(samples.length - 1, localTime * cameraTimeline.sampleRate);
  const index = Math.floor(position);
  const from = samples[index];
  const to = samples[Math.min(index + 1, samples.length - 1)];
  const blend = position - index;
  for (const key of ['targetX', 'targetY', 'targetZ', 'targetFov', 'targetRotZ']) {
    output[key] = from[key] + (to[key] - from[key]) * blend;
  }
  return output;
};
